// Exportar para o Google Earth (KMZ): as regras que não dependem do
// viewer, para se poderem testar.
//
// Pedido do Rui (15/10/2026, Cha139): um só KMZ com os modelos todos, e
// o edifício à altura certa. O exportador assentava no terreno o PONTO
// MAIS BAIXO da geometria; os modelos do Revit trazem marcadores de
// coordenação na origem, à cota 0 ("triângulo de coordenadas
// partilhadas", "marcador de origem da federação"), e o edifício, com o
// Piso 1 a 69,32 m, ficava 69 m no ar.

/** Quantos metros abaixo do grosso dos elementos ainda contam como
 *  base legítima (fundações, poços de elevador, rampas). */
export const FOLGA_DA_BASE_M = 2

/**
 * A cota a pousar no terreno: a base da maioria dos elementos, sem os
 * poucos que ficam muito abaixo dela (marcadores de coordenação na
 * origem, um vértice solto). Recebe a cota mais baixa de cada elemento.
 *
 * Regra: põe-se de lado 2% dos elementos (pelo menos 5, mas nunca mais
 * de 10%), toma-se a cota do primeiro que fica, e a base é o
 * elemento mais baixo que esteja até `FOLGA_DA_BASE_M` abaixo dela. Os
 * que ficam abaixo da base vão enterrados, e diz-se quantos são.
 */
export function cotaDeAssentamento(
  fundos: readonly number[],
): { cota: number; abaixo: number } | null {
  const v = fundos.filter((x) => Number.isFinite(x)).slice().sort((a, b) => a - b)
  if (v.length === 0) return null
  if (v.length < 4) return { cota: v[0], abaixo: 0 }
  const fora = Math.min(Math.max(Math.ceil(v.length * 0.02), 5), Math.floor(v.length * 0.1))
  const referencia = v[fora]
  const limite = referencia - FOLGA_DA_BASE_M
  const i = v.findIndex((x) => x >= limite)
  return { cota: v[i], abaixo: i }
}

/** A cota mais baixa de cada elemento com geometria (frame do viewer),
 *  para `cotaDeAssentamento`. Vazio se o modelo não as der. */
export async function fundosDosElementos(model: unknown): Promise<number[]> {
  const src = model as {
    getItemsIdsWithGeometry?: () => Promise<number[]>
    getBoxes?: (ids: number[]) => Promise<Array<{ min: { y: number } } | null>>
  }
  if (!src.getItemsIdsWithGeometry || !src.getBoxes) return []
  try {
    const ids = await src.getItemsIdsWithGeometry()
    if (!ids?.length) return []
    const boxes = await src.getBoxes(ids)
    return (boxes ?? []).map((b) => b?.min.y ?? Number.NaN).filter((y) => Number.isFinite(y))
  } catch {
    return []
  }
}

export type ModoDaAltitude = 'terreno' | 'absoluta'

/** A altitude do `<Location>` de um modelo no KML.
 *  - absoluta: as cotas do IFC são altitudes (o modelo declara o datum
 *    vertical); a âncora vai à cota da base do modelo;
 *  - terreno: a cota de assentamento comum fica no chão do Google
 *    Earth, e cada modelo vai à sua diferença para ela (os modelos da
 *    federação não se desalinham na vertical). */
export function altitudeDoModelo(
  modo: ModoDaAltitude,
  baseDoModelo: number,
  cotaDeAssentamento: number,
): { altitudeMode: 'absolute' | 'relativeToGround'; alt: number } {
  return modo === 'absoluta'
    ? { altitudeMode: 'absolute', alt: baseDoModelo }
    : { altitudeMode: 'relativeToGround', alt: baseDoModelo - cotaDeAssentamento }
}

/** O nome curto de um modelo para a camada e para o .dae: a
 *  especialidade do código do ficheiro (ARQ, EST, MAC...), senão o nome
 *  sem extensão. Repetidos ganham sufixo. */
export function nomesDasCamadas(ficheiros: readonly string[]): string[] {
  const usados = new Map<string, number>()
  return ficheiros.map((f) => {
    const base = f.replace(/\.(ifc|ifczip|frag)$/i, '')
    const disc = base.match(/-(?:MD3|M3D|MOD)-([A-Z]{2,4})-/i)?.[1]?.toUpperCase() ?? base
    const n = (usados.get(disc) ?? 0) + 1
    usados.set(disc, n)
    return n === 1 ? disc : `${disc}_${n}`
  })
}

/** Um nome de ficheiro seguro dentro do KMZ (files/<nome>.dae). */
export function nomeDeFicheiro(nome: string): string {
  return nome.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9_.-]+/g, '_').slice(0, 80) || 'modelo'
}
